import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Facebook, Instagram, Youtube, ChevronLeft, ChevronRight } from 'lucide-react';

const getPublicUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) return path;
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  const encodedPath = cleanPath.split('/').map(segment => encodeURIComponent(segment)).join('/');
  const base = process.env.PUBLIC_URL || '.';
  return `${base}/${encodedPath}`;
};

const handleImageError = (fallbackPath) => (e) => {
  e.currentTarget.onerror = null;
  if (fallbackPath) {
    e.currentTarget.src = getPublicUrl(fallbackPath);
  }
};

const sections = ['home', 'services', 'about', 'team', 'story', 'gallery', 'videos', 'reviews', 'contact'];

const navItems = [
  { label: 'HOME', sectionIndex: 0 },
  { label: 'ABOUT', sectionIndex: 2 },
  { label: 'TEAM', sectionIndex: 3 },
  { label: 'GALLERY', sectionIndex: 5 },
  { label: 'VIDEOS', sectionIndex: 6 },
  { label: 'REVIEWS', sectionIndex: 7 },
  { label: 'CONTACT', sectionIndex: 8 }
];

const App = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showToTop, setShowToTop] = useState(false);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const [currentReviewsIndex, setCurrentReviewsIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef(null);

  // Touch/Swipe states for gallery
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const getVisibleItems = () => {
    if (windowWidth >= 1024) return 3;
    if (windowWidth >= 640) return 2;
    return 1;
  };

  const visibleItems = getVisibleItems();

  // Touch handlers for gallery swipe
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 75) {
      setCurrentGalleryIndex((prev) => (prev + 1) % 8);
    }
    if (touchStart - touchEnd < -75) {
      setCurrentGalleryIndex((prev) => (prev - 1 + 8) % 8);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const sectionIndex = Math.floor(scrollPosition / windowHeight);
      setActiveSection(Math.min(sectionIndex, sections.length - 1));
      setShowToTop(scrollPosition > 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(err => {
          console.warn('Video autoplay prevented; poster will show:', err);
        });
      }
    }
  }, []);

  const reviews = [
    { name: "Alphius Gonsalves", date: "2025-08-23", text: "A great performance. These guys rocked and had all the crowd dancing to their tunes. A great entertainers. There energy was infectious.", image: "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100" },
    { name: "godfrey fernandes", date: "2025-07-10", text: "We enjoyed last Sunday in Verna awesome outstanding Band *Safarnama BAND* ⭐⭐⭐⭐⭐ Good job guys", image: "https://images.unsplash.com/photo-1599566150163-29194dcaad36?w=100" },
    { name: "Sherlaine Verdes", date: "2025-03-11", text: "Too much Energy, Damn good band, Amazing music and co-ordination", image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100" }
  ];

  // Auto-rotate gallery - slower (5 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentGalleryIndex((prevIndex) => {
        const nextIndex = prevIndex + 1;
        return nextIndex >= 8 ? 0 : nextIndex;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Auto-rotate reviews
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentReviewsIndex((prevIndex) => {
        const nextIndex = prevIndex + 1;
        return nextIndex > reviews.length - visibleItems ? 0 : nextIndex;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [reviews.length, visibleItems]);

  const nextReview = () => {
    setCurrentReviewsIndex((prev) => (prev + 1) % reviews.length);
  };

  const prevReview = () => {
    setCurrentReviewsIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  const scrollToSection = (index) => {
    const sectionId = sections[index];
    const element = document.getElementById(sectionId);
    if (element) {
      window.scrollTo({
        top: element.offsetTop,
        behavior: 'smooth'
      });
    }
    setMenuOpen(false);
  };

  return (
    <div className="relative bg-black text-white">
      {loading && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <span className="loader"></span>
        </div>
      )}
      {/* Left Sidebar - Black Strip with Social Icons */}
      <div className="fixed left-0 top-0 bottom-0 w-[30px] bg-black/80 backdrop-blur-sm z-50 hidden md:flex flex-col items-center justify-center border-r border-white/10">
        <div className="flex flex-col space-y-6">
          <a href="https://www.facebook.com/share/184J12VmFo/" target="_blank" rel="noreferrer" className="text-white/70 hover:text-white transition-colors transform hover:scale-110">
            <Facebook size={14} strokeWidth={1.5} />
          </a>
          <a href="https://www.instagram.com/safarnama.project?igsh=OTZrYmhhZGRpcG0w" target="_blank" rel="noreferrer" className="text-white/70 hover:text-white transition-colors transform hover:scale-110">
            <Instagram size={14} strokeWidth={1.5} />
          </a>
          <a href="https://www.youtube.com/@safarnamaproject" target="_blank" rel="noreferrer" aria-label="Safarnama on YouTube" className="text-white/70 hover:text-white transition-colors transform hover:scale-110">
            <Youtube size={14} strokeWidth={1.5} />
          </a>
        </div>
      </div>

      {/* Right Sidebar - Black Strip with Navigation Dots */}
      <div className="fixed right-0 top-0 bottom-0 w-[30px] bg-black/80 backdrop-blur-sm z-50 hidden md:flex flex-col items-center justify-center border-l border-white/10">
        <div className="flex flex-col space-y-4">
          {sections.map((_, idx) => (
            <button
              key={idx}
              onClick={() => scrollToSection(idx)}
              className={`rounded-full transition-all duration-300 ${activeSection === idx
                ? 'w-2 h-2 bg-white'
                : 'w-1 h-1 bg-white/30 hover:bg-white/50'
                }`}
            />
          ))}
        </div>
      </div>

      {/* Main Content Wrapper - Added padding for sidebars */}
      <div className="md:px-[30px]">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-40 bg-black/80 backdrop-blur-sm md:left-[30px] md:right-[30px]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
            <div className="flex items-center">
              <img
                src={getPublicUrl('logo.png')}
                alt="Safarnama"
                className="h-8 sm:h-10 md:h-12"
                onError={handleImageError('Home_logo.png')}
              />
            </div>

            <div className="hidden lg:flex space-x-6 xl:space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.sectionIndex)}
                  className={`text-xs xl:text-sm font-bold tracking-wider hover:text-yellow-600 transition-colors ${activeSection === item.sectionIndex ? 'text-yellow-600' : 'text-white'
                    }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <button className="lg:hidden" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {menuOpen && (
            <div className="lg:hidden bg-black/95 px-4 sm:px-6 py-4">
              {navItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.sectionIndex)}
                  className="block w-full text-left py-3 text-sm font-bold tracking-wider hover:text-yellow-600 transition-colors"
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </nav>

        {/* Section 1: Home */}
        <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-black overflow-hidden">
            {/* Video Background */}
            <video
              ref={videoRef}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              poster={getPublicUrl('background-fallback.jpg')}
              className="absolute top-0 left-0 w-full h-full object-cover"
              style={{
                opacity: 0.9,
                minWidth: '100%',
                minHeight: '100%',
                width: '100%',
                height: '100%',
                zIndex: 1,
                filter: 'contrast(1.1) saturate(1.2) brightness(1.1)',
                objectFit: 'cover',
                objectPosition: 'center'
              }}
              onError={(e) => {
                console.warn('Video failed to load; poster will display.', e);
              }}
            >
              <source src={getPublicUrl('background-video2.mp4')} type="video/mp4; codecs=avc1.640028" />
              <source src={getPublicUrl('background-video.webm')} type="video/webm; codecs=vp9" />
              <source src="https://videos.pexels.com/video-files/3045163/3045163-uhd_2560_1440_25fps.mp4" type="video/mp4" />
            </video>

            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-orange-900/20 via-black/40 to-black" style={{ zIndex: 2 }}></div>
            {/* Fallback background image */}
            <div
              className="absolute inset-0 bg-cover bg-center z-0"
              style={{
                backgroundImage: `url(${getPublicUrl('background-fallback.jpg')})`,
                opacity: 0.3
              }}
            ></div>
          </div>
          <div className="relative z-10 text-center px-4 sm:px-6">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-light mb-4 sm:mb-6 tracking-wide">WE ARE</h2>
            <img
              src={getPublicUrl('Home_logo.png')}
              alt="Safarnama"
              className="h-20 sm:h-32 md:h-40 lg:h-48 mb-6 sm:mb-8 mx-auto"
              onError={handleImageError('logo.png')}
            />
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                onClick={() => scrollToSection(2)}
                className="px-6 sm:px-8 py-2.5 sm:py-3 bg-[#2d2d2d] hover:bg-[#3d3d3d] text-white transition-all font-bold tracking-wider rounded-md shadow-lg text-sm sm:text-base"
                style={{
                  fontFamily: 'sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              >
                ABOUT US
              </button>
              <button
                onClick={() => scrollToSection(6)}
                className="px-6 sm:px-8 py-2.5 sm:py-3 text-white transition-all font-bold tracking-wider rounded-md shadow-lg text-sm sm:text-base"
                style={{
                  background: 'linear-gradient(135deg, #d4a574 0%, #b8864f 100%)',
                  fontFamily: 'sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #e4b584 0%, #c8965f 100%)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #d4a574 0%, #b8864f 100%)';
                }}
              >
                OUR WORK
              </button>
            </div>
          </div>
        </section>

        {/* Section 2: Services */}
        <section id="services" className="relative min-h-screen bg-black">
          <div className="cards-section">

            {/* LEFT CARD */}
            <div className="event-card club group">
              <div className="event-box">
                {/* Top Part: Header */}
                <div className="event-box-header">
                  <div className="event-title text-white">CLUB EVENTS &amp; FESTIVALS</div>
                  <div className="event-subtitle white">ELECTRIC ENERGY</div>
                </div>
                {/* Bottom Part: Content */}
                <div className="event-box-body">
                  <p>
                    A power-packed performance to leave you exhilarated.
                  </p>
                  <p>
                    Engaging musical sets for <span>festivals</span> and <span>public shows</span>,
                    including <span>carnivals</span>, <span>cultural festivals</span>,
                    <span>holiday specials</span>, and <span>large-scale concerts</span>.
                  </p>
                </div>
              </div>
            </div>

            {/* CENTER CARD */}
            <div className="event-card corporate group">
              <div className="event-box">
                {/* Top Part: Header */}
                <div className="event-box-header">
                  <div className="event-title text-white">CORPORATE EVENTS &amp; SHOWS</div>
                  <div className="event-subtitle white">VIBRANT MUSIC</div>
                </div>
                {/* Bottom Part: Content */}
                <div className="event-box-body">
                  <p>
                    Go global with music for every palette.
                  </p>
                  <p>
                    Live musical sets for <span>corporate events</span> and <span>brand launches</span>,
                    including <span>high-profile launches</span>, <span>corporate galas</span>,
                    <span>award nights</span>, and <span>conferences</span>.
                  </p>
                </div>
              </div>
            </div>

            {/* RIGHT CARD */}
            <div className="event-card wedding group">
              <div className="event-box">
                {/* Top Part: Header */}
                <div className="event-box-header">
                  <div className="event-title text-white">WEDDINGS / PRIVATE PARTIES</div>
                  <div className="event-subtitle white">NOSTALGIC RHYTHMS</div>
                </div>
                {/* Bottom Part: Content */}
                <div className="event-box-body">
                  <p>
                    Music to create your forever memories.
                  </p>
                  <p>
                    Crafting intimate live sets for <span>luxury weddings</span> across India and abroad,
                    including <span>sangeets</span>, <span>receptions</span>, and
                    <span>private family events</span>, as well as for
                    <span>birthdays</span>, <span>anniversaries</span>, and
                    <span>elite soirées</span>.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* Section 3: About */}
        <section id="about" className="relative min-h-screen flex items-center justify-center py-12 sm:py-16 md:py-20 bg-black overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=1920"
              alt="Band Performance"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-4 sm:mb-6 masked-text">
              AB<span className="text-yellow-600">OU</span>T SAFARNAMA
            </h2>
            <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8">MORE THAN <span className="text-yellow-600">MUSIC</span>, IT'S AN EXPERIENCE</p>
            <p className="text-sm sm:text-base md:text-lg text-gray-300 leading-relaxed px-2">
              <span className="font-bold">Safarnama</span> is a dynamic and versatile live band known for delivering high-energy performances that elevate every event. We have enthralled people all over the world with our unmatchable Talent and Performances.
            </p>
          </div>
        </section>

        {/* Section 4: Team */}
        <section id="team" className="relative min-h-screen flex items-center justify-center py-12 sm:py-16 md:py-20 bg-gradient-to-b from-black to-gray-900 border-y border-white/5">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 w-full">
            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-3 sm:mb-4 text-center masked-text">
              TH<span className="text-yellow-600">E</span> TEAM
            </h2>
            <p className="text-center text-base sm:text-lg md:text-xl mb-8 sm:mb-12">WHO <span className="text-yellow-600">ARE WE?</span></p>
            <p className="text-center text-sm sm:text-base text-gray-300 mb-8 sm:mb-12 max-w-3xl mx-auto px-2">
              Our success depends on the <span className="text-yellow-600">strength of our members</span>. The band consists of talented musicians and performers who work together to create a captivating musical experience.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 sm:gap-8 justify-items-center">
              {[
                {
                  name: "REHAN KHAN",
                  role: "SINGER",
                  experience: "15 YEARS+",
                  image: getPublicUrl("Team/Rehan khan singer.jpeg"),
                  bio: "A versatile vocalist with a soulful voice that captures the essence of every performance."
                },
                {
                  name: "RASHAD ALI",
                  role: "BOLLYWOOD SINGER",
                  experience: "15 YEARS+",
                  image: getPublicUrl("Team/Singer Rashad Ali Bollywood singer.jpeg"),
                  bio: "Renowned for his powerful stage presence and incredible vocal range in Bollywood music."
                },
                {
                  name: "HARSH KUMAR",
                  role: "DRUMMER",
                  experience: "15 YEARS+",
                  image: getPublicUrl("Team/Harsh Kumar drumist.JPEG"),
                  bio: "A rhythmic master who brings unmatched energy and precision to every live show."
                },
                {
                  name: "JAY RAQ",
                  role: "BASS GUITARIST",
                  experience: "12 YEARS+",
                  image: getPublicUrl("Team/Jay raq bass Guitarist.jpeg"),
                  bio: "Exceptional prowess as a bass player, providing the deep, groovy heartbeat of the band."
                },
                {
                  name: "TARUN GUPTA",
                  role: "LEAD GUITARIST",
                  experience: "8 YEARS+",
                  image: getPublicUrl("Team/Tarun Gupta lead Guitarist.jpeg"),
                  bio: "A maestro of strings and melodies, whose guitar riffs are the soul of our performances."
                },
                {
                  name: "KD SHARMA",
                  role: "MUSICIAN",
                  experience: "8 YEARS+",
                  image: getPublicUrl("Team/kd sharma.jpg"),
                  bio: "A talented musician bringing unique sounds and creative energy to our performances."
                }
              ].map((member, i) => (
                <div key={i} className="flex flex-col items-center group w-full max-w-[240px]">
                  {/* Flip Card Container */}
                  <div className="w-full aspect-[3/4] perspective-1000 cursor-pointer mb-6 transform transition-transform duration-500 group-hover:scale-[1.05]">
                    <div className="flip-card-inner shadow-xl rounded-lg">

                      {/* Front Face: Image */}
                      <div className="flip-card-front bg-gradient-to-br from-yellow-600 to-yellow-800 rounded-lg relative">
                        <img
                          src={member.image}
                          alt={member.name}
                          loading="lazy"
                          className="w-full h-full object-cover"
                          onError={handleImageError('background-fallback.jpg')}
                        />
                        <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors"></div>
                      </div>

                      {/* Back Face: White with Text & Icons */}
                      <div className="flip-card-back bg-white text-black flex flex-col justify-center items-center p-4 text-center rounded-lg bg-opacity-95 backdrop-blur-sm border border-gray-200">
                        <div className="mb-3 opacity-0 animate-fadeIn" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
                          <Facebook size={20} className="text-black mb-2 mx-auto" strokeWidth={1.5} />
                        </div>
                        <p className="text-xs sm:text-sm font-medium leading-relaxed mb-2 text-gray-800 font-serif tracking-wide italic line-clamp-4">
                          "{member.bio}"
                        </p>
                        <p className="text-[10px] font-bold text-yellow-700 tracking-widest uppercase mb-4">
                          {member.experience} EXPERIENCE
                        </p>

                        <div className="flex space-x-4 justify-center mt-auto pb-2">
                          <a href="https://www.facebook.com/share/184J12VmFo/" target="_blank" rel="noreferrer" className="text-black hover:text-yellow-600 transition-colors transform hover:scale-110">
                            <Facebook size={20} />
                          </a>
                          <a href="https://www.instagram.com/safarnama.project?igsh=OTZrYmhhZGRpcG0w" target="_blank" rel="noreferrer" className="text-black hover:text-yellow-600 transition-colors transform hover:scale-110">
                            <Instagram size={20} />
                          </a>
                        </div>
                      </div>

                    </div>
                  </div>

                  {/* Name and Role (Outside Flip) */}
                  <h3 className="text-white font-black tracking-widest text-base sm:text-lg md:text-xl uppercase mb-1 text-center">
                    {member.name}
                  </h3>
                  <p className="text-yellow-500 text-[10px] sm:text-xs md:text-sm font-bold tracking-[0.2em] uppercase text-center">
                    {member.role}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 5: Story */}
        <section id="story" className="relative min-h-screen flex items-center justify-center py-12 sm:py-16 md:py-20 bg-black">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center">
            <div className="aspect-[4/3] bg-gradient-to-br from-gray-800 to-black rounded-lg overflow-hidden order-2 md:order-1">
              <img
                src={getPublicUrl('OurStory.jpeg')}
                alt="Safarnama Band Story"
                loading="lazy"
                className="w-full h-full object-cover"
                onError={handleImageError('background-fallback.jpg')}
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black mb-4 sm:mb-6 masked-text">
                <span className="text-yellow-600">OUR</span> STORY
              </h2>
              <p className="text-base sm:text-lg md:text-xl mb-4 sm:mb-6 text-yellow-600">*OUR GOAL IS SIMPLE, TO MAKE MUSIC*</p>
              <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-3 sm:mb-4">
                The crashing waves on pristine shores and swaying trees along the coastlines - Goa cradles music! This music runs in our veins and is nurtured every single day. Its genesis for almost all of us has been at home, school, or church. These nascent stages have contributed greatly to our professional presence.
              </p>
              <p className="text-sm sm:text-base text-gray-300 leading-relaxed">
                All of us, at varying points, played for bands across Goa, and what we bring today is a confluence of our experiences and talent. We believe in our chemistry, and this helps us let the music shine. Every nod in the direction of a team member, every wink of an eye, or an impromptu performance of a song request has, brick by brick, laid the foundations of 24K. So join us on this journey, where we let the music take control.
              </p>
            </div>
          </div>
        </section>

        {/* Section 6: Gallery */}
        {/* Section 6: Gallery */}
        <section id="gallery" className="relative min-h-screen flex items-center justify-center py-8 sm:py-12 md:py-16 bg-gradient-to-b from-gray-900 to-black overflow-hidden">
          <div className="w-full mx-auto px-2 sm:px-4 md:px-6">
            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-2 sm:mb-3 md:mb-4 text-center masked-text">
              <span className="text-yellow-600">GA</span>LLERY
            </h2>
            <p className="text-center text-base sm:text-lg md:text-xl mb-4 sm:mb-6 md:mb-8">SOME RECENT <span className="text-yellow-600">PROJECTS</span></p>

            {/* Carousel Container */}
            <div className="relative w-full">
              {/* Navigation Buttons - Desktop Only */}
              <button
                onClick={() => setCurrentGalleryIndex((prev) => (prev - 1 + 8) % 8)}
                className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={() => setCurrentGalleryIndex((prev) => (prev + 1) % 8)}
                className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
              >
                <ChevronRight size={24} />
              </button>

              {/* Carousel */}
              <div
                className="relative w-full overflow-hidden touch-pan-x"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <div
                  className="flex transition-transform duration-1000 ease-in-out"
                  style={{
                    transform: `translateX(-${currentGalleryIndex * (100 / visibleItems)}%)`
                  }}
                >
                  {[
                    getPublicUrl('Gallery/g1.jpeg'),
                    getPublicUrl('Gallery/g2.jpeg'),
                    getPublicUrl('Gallery/g3.jpeg'),
                    getPublicUrl('Gallery/g4.PNG'),
                    getPublicUrl('Gallery/g5.jpeg'),
                    getPublicUrl('Gallery/g6.jpeg'),
                    getPublicUrl('Gallery/g7.jpeg'),
                    getPublicUrl('Gallery/g9.jpeg'),
                    // Duplicates for seamless loop
                    getPublicUrl('Gallery/g1.jpeg'),
                    getPublicUrl('Gallery/g2.jpeg'),
                    getPublicUrl('Gallery/g3.jpeg')
                  ].map((img, i) => (
                    <div
                      key={i}
                      className="flex-shrink-0 w-full sm:w-1/2 md:w-1/3 px-1 sm:px-2"
                    >
                      <div className="aspect-[16/10] bg-gradient-to-br from-yellow-600 to-orange-800 rounded-lg overflow-hidden group">
                        <img
                          src={img}
                          alt={`Performance ${(i % 8) + 1}`}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          onError={handleImageError('background-fallback.jpg')}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Touch Swipe Navigation for Mobile */}
              <div className="md:hidden flex justify-between mt-4 px-4">
                <button
                  onClick={() => setCurrentGalleryIndex((prev) => (prev - 1 + 8) % 8)}
                  className="bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={() => setCurrentGalleryIndex((prev) => (prev + 1) % 8)}
                  className="bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>

            {/* Gallery Indicators */}
            <div className="flex justify-center mt-6 sm:mt-8 gap-2">
              {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                <button
                  key={i}
                  onClick={() => setCurrentGalleryIndex(i)}
                  className={`h-2 rounded-full transition-all ${currentGalleryIndex === i
                    ? 'w-8 bg-yellow-600'
                    : 'w-2 bg-white/30 hover:bg-white/50'
                    }`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Section 7: Videos */}
        <section id="videos" className="relative min-h-screen flex items-center justify-center py-12 sm:py-16 md:py-20 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-3 sm:mb-4 text-center masked-text">
              <span className="text-yellow-600">VI</span>DEOS
            </h2>
            <p className="text-center text-base sm:text-lg md:text-xl mb-8 sm:mb-12">A <span className="text-yellow-600">REPERTOIRE</span> OF OUR WORK</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              {[
                'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=800',
                'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800',
                'https://images.unsplash.com/photo-1516280440614-37939bbacd81?w=800',
                'https://images.unsplash.com/photo-1511578314322-379afb476865?w=800'
              ].map((img, i) => (
                <div key={i} className="aspect-video bg-gradient-to-br from-purple-900 to-black rounded-lg overflow-hidden relative group">
                  <img
                    src={img}
                    alt={`Video ${i + 1}`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    onError={handleImageError('background-fallback.jpg')}
                  />
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Youtube size={48} className="sm:w-16 sm:h-16 text-white" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 8: Reviews */}
        <section id="reviews" className="relative min-h-screen flex items-center justify-center py-12 sm:py-16 md:py-20 bg-black overflow-hidden">
          <div className="absolute inset-0 z-0">
            {/* Background Image */}
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?w=1600')] bg-cover bg-center opacity-20"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-transparent"></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full">
            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-3 sm:mb-4 text-center masked-text">
              <span className="text-yellow-600">REVI</span>EWS
            </h2>
            <p className="text-center text-base sm:text-lg md:text-xl mb-8 sm:mb-12">OUR HAPPY <span className="text-yellow-600">CLIENTS</span></p>

            <div className="relative flex items-center justify-center">
              {/* Previous Button */}
              <button
                onClick={prevReview}
                className="absolute left-0 z-20 p-2 text-white/50 hover:text-yellow-600 transition-colors"
              >
                <ChevronLeft size={32} className="sm:w-12 sm:h-12" />
              </button>

              {/* Reviews Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full px-8 md:px-12">
                {[...Array(visibleItems)].map((_, offset) => {
                  const index = (currentReviewsIndex + offset) % reviews.length;
                  const review = reviews[index];
                  return (
                    <div key={index} className="bg-[#1a1a1a] p-8 rounded-lg relative flex flex-col items-center text-center group hover:bg-[#222] transition-colors border border-white/5">
                      {/* Avatar */}
                      <div className="absolute -top-10">
                        <div className="relative">
                          <img
                            src={review.image}
                            alt={review.name}
                            loading="lazy"
                            className="w-20 h-20 rounded-full border-4 border-[#1a1a1a] object-cover"
                            onError={handleImageError('logo.png')}
                          />
                          <div className="absolute bottom-0 right-0 bg-white rounded-full p-1 shadow-md">
                            <svg className="w-4 h-4" viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
                              <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
                                <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z" />
                                <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z" />
                                <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.734 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z" />
                                <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.424 44.599 -10.174 45.789 L -6.704 42.299 C -8.804 40.309 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z" />
                              </g>
                            </svg>
                          </div>
                        </div>
                      </div>

                      <div className="mt-10 mb-2">
                        <h4 className="font-bold text-white text-lg">{review.name}</h4>
                        <p className="text-gray-500 text-xs tracking-wider uppercase">{review.date}</p>
                      </div>

                      <div className="flex mb-4">
                        {[...Array(5)].map((_, i) => (
                          <span key={i} className="text-[#FFB400] text-lg">★</span>
                        ))}
                      </div>

                      <p className="text-gray-300 text-sm leading-relaxed line-clamp-4">
                        {review.text}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Next Button */}
              <button
                onClick={nextReview}
                className="absolute right-0 z-20 p-2 text-white/50 hover:text-yellow-600 transition-colors"
              >
                <ChevronRight size={32} className="sm:w-12 sm:h-12" />
              </button>
            </div>

            {/* Mobile Indicators */}
            <div className="flex md:hidden justify-center mt-8 gap-2">
              {reviews.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentReviewsIndex(i)}
                  className={`h-2 rounded-full transition-all ${currentReviewsIndex === i
                    ? 'w-6 bg-yellow-600'
                    : 'w-2 bg-white/30'
                    }`}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Section 9: Contact */}
        <section id="contact" className="relative min-h-screen flex items-center justify-center py-12 sm:py-16 md:py-20 bg-black overflow-hidden">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1600"
              alt="Crowd Background"
              loading="lazy"
              className="w-full h-full object-cover opacity-40 blur-[2px]"
            />
            <div className="absolute inset-0 bg-black/60"></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-6 w-full h-full flex flex-col justify-center">
            {/* Header Centered */}
            <div className="text-center mb-10 sm:mb-16">
              <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-2 sm:mb-4 masked-text tracking-tighter">
                CO<span className="text-yellow-600">N</span>TACT
              </h2>
              <p className="text-base sm:text-lg md:text-xl font-bold tracking-[0.2em] text-white">KEEP IN TOUCH</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 md:gap-32 items-start">
              {/* Left Column: Contact Info */}
              <div className="flex flex-col items-center text-center space-y-4 pt-4 md:pr-16">
                <div className="flex flex-col items-center space-y-2">
                  <p className="text-sm sm:text-base font-bold tracking-widest text-white">
                    TEL.: <span className="text-yellow-600">8630504917</span>
                  </p>
                  <a href="mailto:contact@safarnamaproject.in" className="text-base sm:text-lg text-white hover:text-yellow-600 transition-colors">
                    Owner Prachi Agrawal,<br />
                    Manager, Mr. Rehan.
                  </a>
                </div>
                <div className="flex justify-center space-x-1">
                  <a href="https://www.facebook.com/share/184J12VmFo/" target="_blank" rel="noreferrer" aria-label="Safarnama on Facebook" className="text-white hover:text-yellow-600 transition-colors transform hover:scale-110"><Facebook size={28} /></a>
                  <a href="https://www.instagram.com/safarnama.project?igsh=OTZrYmhhZGRpcG0w" target="_blank" rel="noreferrer" aria-label="Safarnama on Instagram" className="text-white hover:text-yellow-600 transition-colors transform hover:scale-110"><Instagram size={28} /></a>
                  <a href="https://www.youtube.com/@safarnamaproject" target="_blank" rel="noreferrer" aria-label="Safarnama on YouTube" className="text-white hover:text-yellow-600 transition-colors transform hover:scale-110"><Youtube size={28} /></a>
                </div>
              </div>

              {/* Right Column: Form */}
              <div className="w-full max-w-lg mx-auto md:mx-0">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Name"
                      className="w-full bg-transparent border border-white/30 px-4 py-3 focus:border-yellow-600 focus:outline-none text-white text-sm placeholder-white/50"
                    />
                    <input
                      type="email"
                      placeholder="Email"
                      className="w-full bg-transparent border border-white/30 px-4 py-3 focus:border-yellow-600 focus:outline-none text-white text-sm placeholder-white/50"
                    />
                  </div>
                  <textarea
                    placeholder="Message"
                    rows="6"
                    className="w-full bg-transparent border border-white/30 px-4 py-3 focus:border-yellow-600 focus:outline-none text-white text-sm resize-none placeholder-white/50"
                  ></textarea>

                  <div className="flex justify-end">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        alert('Thank you! Your message has been received.');
                      }}
                      className="px-8 py-2 border border-yellow-600 text-yellow-600 hover:bg-yellow-600 hover:text-white transition-all font-bold tracking-[0.2em] text-sm uppercase"
                    >
                      SEND
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gradient-to-b from-black to-gray-900 py-8 sm:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 text-center md:text-left items-center">
              <div>
                <h3 className="font-bold mb-3 sm:mb-4 text-sm sm:text-base">ABOUT <span className="text-yellow-600">US</span></h3>
                <p className="text-xs sm:text-sm text-gray-400">Safarnama Music Band is an exceptional choice for live events, weddings, and all other occasions.</p>
                <div className="flex justify-center md:justify-start space-x-4 mt-3 sm:mt-4">
                  <a href="https://www.facebook.com/share/184J12VmFo/" target="_blank" rel="noreferrer" aria-label="Safarnama on Facebook" className="hover:text-yellow-600 transition-colors"><Facebook size={18} className="sm:w-5 sm:h-5" /></a>
                  <a href="https://www.instagram.com/safarnama.project?igsh=OTZrYmhhZGRpcG0w" target="_blank" rel="noreferrer" aria-label="Safarnama on Instagram" className="hover:text-yellow-600 transition-colors"><Instagram size={18} className="sm:w-5 sm:h-5" /></a>
                  <a href="https://www.youtube.com/@safarnamaproject" target="_blank" rel="noreferrer" aria-label="Safarnama on YouTube" className="hover:text-yellow-600 transition-colors"><Youtube size={18} className="sm:w-5 sm:h-5" /></a>
                </div>
              </div>

              <div className="flex justify-center">
                <img
                  src={getPublicUrl('Home_logo.png')}
                  alt="Safarnama"
                  loading="lazy"
                  className="h-24 sm:h-28 md:h-32"
                  onError={handleImageError('logo.png')}
                />
              </div>

              <div className="md:text-right">
                <h3 className="font-bold mb-3 sm:mb-4 text-sm sm:text-base">GET IN <span className="text-yellow-600">TOUCH</span></h3>
                <p className="text-xs sm:text-sm text-gray-400 mb-2">TEL.: <a href="tel:+918630504917" className="text-yellow-600 hover:underline break-all">+91 86305 04917</a></p>
                <p className="text-xs sm:text-sm text-gray-400 mb-2">MARGAO, GOA</p>
                <p className="text-xs sm:text-sm text-gray-400"><a href="mailto:contact@safarnamaproject.in" className="text-yellow-600 hover:underline break-all">contact@safarnamaproject.in</a></p>
              </div>
            </div>

            <div className="border-t border-gray-800 mt-6 sm:mt-8 pt-6 sm:pt-8 text-center text-xs sm:text-sm text-gray-500">
              <p>Copyright © 2025 Safarnama</p>
            </div>
          </div>
        </footer>

        {/* Scroll Down / To Top Button - Fixed at Bottom Center */}
        {/* Scroll Down / To Top Bar - Fixed at Bottom */}
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-sm border-t border-gray-800 py-1 flex justify-center items-center md:left-[30px] md:right-[30px]">
          <button
            onClick={() => {
              if (showToTop) {
                scrollToSection(0);
              } else {
                window.scrollBy({ top: window.innerHeight, behavior: 'smooth' });
              }
            }}
            className="text-white text-[10px] sm:text-xs tracking-[0.2em] font-bold transition-all cursor-pointer hover:text-yellow-500 uppercase flex items-center gap-2"
            style={{
              fontFamily: 'sans-serif'
            }}
          >
            {showToTop ? (
              <>
                TO <span className="text-yellow-600">TOP</span>
                <span className="text-yellow-600">↑</span>
              </>
            ) : (
              <>
                SCROLL <span className="text-yellow-600">DOWN</span>
                <span className="text-yellow-600">↓</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default App;